import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { MqttService, MqttMessage } from './mqtt.service';

export interface Notification {
  id: string;
  timestamp: Date;
  topic: string;
  ruleCode: string;
  subject: string;
  type: 'up' | 'down';
  severity: 'info' | 'warning' | 'critical';
  description: string;
  payload: any;
  read: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationListenerService {
  private notifications: Notification[] = [];
  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  private isListeningSubject = new BehaviorSubject<boolean>(false);
  private unreadCountSubject = new BehaviorSubject<number>(0);

  public notifications$ = this.notificationsSubject.asObservable();
  public isListening$ = this.isListeningSubject.asObservable();
  public unreadCount$ = this.unreadCountSubject.asObservable();

  private mqttSubscription: any;
  private currentTopic: string | null = null;

  constructor(private mqttService: MqttService) {
    // Subscribe to MQTT messages
    this.mqttSubscription = this.mqttService.messages$.subscribe(msg => {
      this.handleMqttMessage(msg);
    });
  }

  /**
   * Start listening to notifications
   */
  startListening(tenant: string, customerId: string, username?: string, password?: string): void {
    if (this.isListeningSubject.value) {
      console.log('[NotificationListener] Already listening');
      return;
    }

    // Connect to broker if not connected
    if (!this.mqttService.isConnected()) {
      this.mqttService.connect({
        username: username || 'admin',
        password: password || 'dariush'
      });

      // Wait for connection
      const connectionSub = this.mqttService.connectionStatus$.subscribe(status => {
        if (status.connected) {
          this.subscribeToTopic(tenant, customerId);
          connectionSub.unsubscribe();
        }
      });
    } else {
      this.subscribeToTopic(tenant, customerId);
    }
  }

  /**
   * Stop listening to notifications
   */
  stopListening(): void {
    if (!this.isListeningSubject.value) {
      return;
    }

    // Unsubscribe from topic
    if (this.currentTopic) {
      this.mqttService.unsubscribe(this.currentTopic);
      this.currentTopic = null;
    }

    this.isListeningSubject.next(false);
    console.log('[NotificationListener] Stopped listening');
  }

  /**
   * Toggle listening
   */
  toggleListening(tenant: string, customerId: string): void {
    if (this.isListeningSubject.value) {
      this.stopListening();
    } else {
      this.startListening(tenant, customerId);
    }
  }

  /**
   * Subscribe to specific topic
   */
  private subscribeToTopic(tenant: string, customerId: string): void {
    this.currentTopic = `notifications/${tenant}/${customerId}/#`;
    this.mqttService.subscribe(this.currentTopic);
    this.isListeningSubject.next(true);
    
    console.log('[NotificationListener] Subscribed to:', this.currentTopic);
  }

  /**
   * Handle incoming MQTT messages
   */
  private handleMqttMessage(msg: MqttMessage): void {
    if (!this.isListeningSubject.value) {
      return;
    }

    try {
      const payload = msg.payload;
      
      const notification: Notification = {
        id: payload.notificationId || `notif-${Date.now()}-${Math.random()}`,
        timestamp: new Date(payload.timestamp || msg.timestamp),
        topic: msg.topic,
        ruleCode: payload.ruleCode || 'UNKNOWN',
        subject: payload.subject || 'N/A',
        type: payload.type === 'down' ? 'down' : 'up',
        severity: this.mapSeverity(payload.severity, payload.type),
        description: payload.event?.description || payload.ruleCode || 'Notification',
        payload: payload,
        read: false
      };

      // Add to beginning (reverse chronological order)
      this.notifications.unshift(notification);

      // Limit to 100 notifications
      if (this.notifications.length > 100) {
        this.notifications = this.notifications.slice(0, 100);
      }

      this.notificationsSubject.next([...this.notifications]);
      this.updateUnreadCount();

      console.log('[NotificationListener] New notification:', notification);

    } catch (err) {
      console.error('[NotificationListener] Error processing notification:', err);
    }
  }

  /**
   * Map severity
   */
  private mapSeverity(severity: string, type: string): 'info' | 'warning' | 'critical' {
    if (type === 'down') {
      return 'info'; // Green for recovery
    }

    const sev = (severity || '').toLowerCase();
    if (sev === 'critical') return 'critical';
    if (sev === 'warning') return 'warning';
    return 'info';
  }

  /**
   * Mark notification as read
   */
  markAsRead(notificationId: string): void {
    const notif = this.notifications.find(n => n.id === notificationId);
    if (notif) {
      notif.read = true;
      this.notificationsSubject.next([...this.notifications]);
      this.updateUnreadCount();
    }
  }

  /**
   * Mark all as read
   */
  markAllAsRead(): void {
    this.notifications.forEach(n => n.read = true);
    this.notificationsSubject.next([...this.notifications]);
    this.updateUnreadCount();
  }

  /**
   * Clear notifications
   */
  clearNotifications(): void {
    this.notifications = [];
    this.notificationsSubject.next([]);
    this.updateUnreadCount();
  }

  /**
   * Update unread count
   */
  private updateUnreadCount(): void {
    const count = this.notifications.filter(n => !n.read).length;
    this.unreadCountSubject.next(count);
  }
}
